import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { settingsService } from '../services/api';
import { useAuth } from './AuthContext';
import { DEFAULT_WIDGET_VISIBILITY, mergeWidgetPreferences } from '../utils/widgetRegistry';

type WidgetVisibilityContextValue = {
  preferences: Record<string, boolean>;
  loading: boolean;
  isVisible: (widgetId: string) => boolean;
  refresh: () => Promise<void>;
  setPreferences: (next: Record<string, boolean>) => void;
};

const WidgetVisibilityContext = createContext<WidgetVisibilityContextValue | null>(null);

export const WidgetVisibilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { hostel } = useAuth();
  const [preferences, setPreferences] = useState<Record<string, boolean>>(DEFAULT_WIDGET_VISIBILITY);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!hostel?.id) {
      setPreferences(DEFAULT_WIDGET_VISIBILITY);
      return;
    }
    setLoading(true);
    try {
      const stored = await settingsService.getDisplayWidgets(hostel.id);
      setPreferences(mergeWidgetPreferences(stored));
    } catch {
      setPreferences(DEFAULT_WIDGET_VISIBILITY);
    } finally {
      setLoading(false);
    }
  }, [hostel?.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const isVisible = useCallback(
    (widgetId: string) => preferences[widgetId] !== false,
    [preferences],
  );

  const value = useMemo(
    () => ({
      preferences,
      loading,
      isVisible,
      refresh: load,
      setPreferences,
    }),
    [preferences, loading, isVisible, load],
  );

  return (
    <WidgetVisibilityContext.Provider value={value}>{children}</WidgetVisibilityContext.Provider>
  );
};

export function useWidgetVisibility(): WidgetVisibilityContextValue {
  const ctx = useContext(WidgetVisibilityContext);
  if (!ctx) {
    return {
      preferences: DEFAULT_WIDGET_VISIBILITY,
      loading: false,
      isVisible: () => true,
      refresh: async () => {},
      setPreferences: () => {},
    };
  }
  return ctx;
}
